import { type ExamRoomRequestComputerAttributes } from '@exam-center/graphql';

import { type ExamLocationRequestRoomComputerInfoFormInputs } from './schema';

export type DeepPartial<T> = T extends (...args: unknown[]) => unknown
  ? T
  : T extends Array<infer U>
    ? Array<DeepPartial<U>>
    : T extends object
      ? { [K in keyof T]?: DeepPartial<T[K]> }
      : T | undefined;

export const MONITOR_SIZE_OPTIONS = ['lt-17-inch', 'ge-17-inch', 'other'] as const;
export const CPU_OPTIONS = [
  'intel-core-i3-ge-2ghz',
  'intel-core-i5-ge-2ghz',
  'intel-core-i7-ge-2ghz',
  'other',
] as const;
export const RAM_SIZE_OPTIONS = ['lt-4gb', '4gb', '8gb', '16gb', 'gt-16gb', 'other'] as const;
export const OS_OPTIONS = ['windows-10', 'windows-11', 'macos-ge-10.14', 'other'] as const;
export const STORAGE_OPTIONS = ['500gb', '1tb', '2tb', 'other'] as const;
export const WEB_BROWSER_OPTIONS = ['google-chrome', 'edge', 'firefox', 'safari', 'other'] as const;
export const SEB_VERSION_STATUS_OPTIONS = ['seb-ge-3.9.3', 'seb-lt-3.9.3'] as const;
export const UPDATE_CADENCE_OPTIONS = ['monthly', 'quarterly', 'semiannual', 'other'] as const;

export const INTERNET_SPEED_OPTIONS = ['100-50', '100-100', '200-50', '200-100', 'other'] as const;

export const ANTIVIRUS_PROGRAM_OPTIONS = [
  'windows-defender',
  'norton',
  // eslint-disable-next-line @cspell/spellchecker
  'kaspersky',
  'avast',
  'other',
] as const;
export const ANTIVIRUS_CONFIG_FOR_SEB_OPTIONS = [
  'allow-seb',
  'not-installed',
  'not-available',
  'not-configured',
] as const;
export const SYNCED_WITH_THAI_SERVER_OPTIONS = ['synced', 'not-synced'] as const;
export const USB_PORT_CHECK_OPTIONS = ['checked', 'not-checked', 'not-available'] as const;

function coerceEnum<V extends readonly string[]>(
  value: string | null | undefined,
  allowed: V,
): V[number] | undefined {
  if (!value) return undefined;
  return (allowed as readonly string[]).includes(value) ? (value as V[number]) : undefined;
}

function coerceWithOther<
  V extends readonly string[],
  S extends { value?: string | null; otherDetail?: string | null } | null | undefined,
>(source: S, allowed: V): { value: V[number]; otherDetail?: string } | undefined {
  if (!source) return undefined;

  const rawValue = source.value ?? undefined;
  const otherDetail = source.otherDetail ?? undefined;

  const value = coerceEnum(rawValue, allowed);
  if (value) {
    return { value, otherDetail };
  }

  if (otherDetail && (allowed as readonly string[]).includes('other')) {
    return { value: 'other' as V[number], otherDetail };
  }

  return undefined;
}

export function mapComputerInfoToDefaultValues(
  attrs?: Partial<ExamRoomRequestComputerAttributes> | null,
): DeepPartial<ExamLocationRequestRoomComputerInfoFormInputs> {
  if (!attrs) return {};

  const { computerDetails: cd, internetDetails: id, securityDetails: sd } = attrs;

  const result: DeepPartial<ExamLocationRequestRoomComputerInfoFormInputs> = {};

  if (cd) {
    const computer: DeepPartial<ExamLocationRequestRoomComputerInfoFormInputs['computerDetails']> =
      {};

    computer.monitor = coerceWithOther(cd.monitor, MONITOR_SIZE_OPTIONS);
    computer.cpu = coerceWithOther(cd.cpu, CPU_OPTIONS);
    computer.ram = coerceWithOther(cd.ram, RAM_SIZE_OPTIONS);
    computer.os = coerceWithOther(cd.os, OS_OPTIONS);
    computer.storage = coerceWithOther(cd.storage, STORAGE_OPTIONS);
    computer.webBrowser = coerceWithOther(cd.webBrowser, WEB_BROWSER_OPTIONS);
    computer.sebVersionStatus = coerceEnum(
      cd.sebVersionStatus ?? undefined,
      SEB_VERSION_STATUS_OPTIONS,
    );
    computer.osUpdateCadence = coerceWithOther(cd.osUpdateCadence, UPDATE_CADENCE_OPTIONS);
    if (cd.computerSpecImages) computer.computerSpecImages = cd.computerSpecImages;
    if (cd.computerSeatImages) computer.computerSeatImages = cd.computerSeatImages;

    // Only set section if any field exists
    if (Object.keys(computer).length > 0) {
      result.computerDetails = computer;
    }
  }

  if (id) {
    const internet: DeepPartial<ExamLocationRequestRoomComputerInfoFormInputs['internetDetails']> =
      {};

    internet.internetSpeed = coerceWithOther(id.internetSpeed, INTERNET_SPEED_OPTIONS);
    if (id.speedTestImages) internet.speedTestImages = id.speedTestImages;

    if (Object.keys(internet).length > 0) {
      result.internetDetails = internet;
    }
  }

  if (sd) {
    const security: DeepPartial<ExamLocationRequestRoomComputerInfoFormInputs['securityDetails']> =
      {};

    security.antivirusProgram = coerceWithOther(sd.antivirusProgram, ANTIVIRUS_PROGRAM_OPTIONS);
    security.antivirusConfigForSEB = coerceEnum(
      sd.antivirusConfigForSEB ?? undefined,
      ANTIVIRUS_CONFIG_FOR_SEB_OPTIONS,
    );
    security.isSyncedWithThaiServer =
      sd.isSyncedWithThaiServer == null
        ? undefined
        : sd.isSyncedWithThaiServer
          ? 'synced'
          : 'not-synced';

    if (sd.timeSyncImages) security.timeSyncImages = sd.timeSyncImages;
    security.usbPortCheck = coerceEnum(sd.usbPortCheck ?? undefined, USB_PORT_CHECK_OPTIONS);
    security.antivirusUpdateCadence = coerceWithOther(
      sd.antivirusUpdateCadence,
      UPDATE_CADENCE_OPTIONS,
    );
    if (sd.robotTestImages) security.robotTestImages = sd.robotTestImages;

    if (Object.keys(security).length > 0) {
      result.securityDetails = security;
    }
  }

  return result;
}
